import PropTypes from 'prop-types';
import React from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { datadogRum } from '@datadog/browser-rum';
import { MIC_FAILURE_TYPES } from '../../trc/pages/instructions/TROInstructionsConstants';
import { convertForAnalysisService } from './recordingUtils';
import { createDevLogger } from '../utils/devLog';

const logger = createDevLogger('RecordingView');

// Record at native sample rate, convert post-recording to avoid device-specific issues
const AUDIO_RECORDER_OPTIONS = {
    type: 'audio',
    recorderType: StereoAudioRecorder,
    bufferSize: 4096,
    numberOfAudioChannels: 1,
};

class RecordingView extends React.PureComponent {
    componentDidMount() {
        if (!this.props.recordingStream) {
            this.resetRecordingStream();
        }
    }

    componentWillUnmount() {
        this.cleanupResources();
    }

    initializeRecorder = async () => {
        const { recordingStream, duration } = this.props;

        try {
            const stream = recordingStream || (await this.resetRecordingStream());
            const streamActive = stream.active;
            const trackCount = stream.getAudioTracks().length;
            const trackStates = stream
                .getAudioTracks()
                .map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState }));

            logger.log(
                `Initializing recorder - Stream active: ${streamActive}, Audio tracks: ${trackCount}`,
                trackStates,
            );

            // Detect dead/inactive stream
            const hasDeadTracks = trackStates.some(t => t.readyState === 'ended');
            if (!streamActive || hasDeadTracks) {
                const deadStreamError = new Error(
                    `Dead MediaStream detected - Active: ${streamActive}, Tracks: ${JSON.stringify(
                        trackStates,
                    )}`,
                );
                deadStreamError.name = 'DeadMediaStreamError';
                datadogRum.addError(deadStreamError);
                logger.error('Dead stream detected, proceeding with recording attempt');
            }

            this.audioRecorder = new RecordRTC(stream, AUDIO_RECORDER_OPTIONS);
            this.audioRecorder
                .setRecordingDuration(duration)
                .onRecordingStopped(this.handleRecordingStopped);
        } catch (error) {
            logger.error('Recorder initialization failed:', error);
            datadogRum.addError(error);
            this.props.setRecordingError(MIC_FAILURE_TYPES.ACCESS_FAILED);
            throw error;
        }
    };

    resetRecordingStream = async () => {
        logger.log('Getting fresh MediaStream from getUserMedia');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const trackStates = stream
            .getAudioTracks()
            .map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState }));
        logger.log('Fresh stream obtained - Active:', stream.active, 'Tracks:', trackStates);
        this.props.setRecordingStream(stream);
        return stream;
    };

    reinitializeRecorder = () => {
        this.audioRecorder = null;
        this.isInitializing = false;
    };

    startRecording = async () => {
        try {
            if (this.isInitializing) return; // Guard against double-click

            if (!this.audioRecorder) {
                this.isInitializing = true;
                await this.initializeRecorder();
                this.isInitializing = false;
            }
            this.audioRecorder.startRecording();
            this.props.startRecording();
        } catch (error) {
            datadogRum.addError(error);
            this.isInitializing = false;
            this.props.setRecordingError(MIC_FAILURE_TYPES.ACCESS_FAILED);
        }
    };

    pauseRecording = () => {
        try {
            this.audioRecorder.pauseRecording();
            this.props.stopRecording();
        } catch (error) {
            datadogRum.addError(error);
            this.props.setRecordingError(MIC_FAILURE_TYPES.ACCESS_FAILED);
        }
    };

    resumeRecording = () => {
        try {
            this.audioRecorder.resumeRecording();
            this.props.startRecording();
        } catch (error) {
            datadogRum.addError(error);
            this.props.setRecordingError(MIC_FAILURE_TYPES.ACCESS_FAILED);
        }
    };

    stopRecording = (onStopped = () => {}) => {
        try {
            this.audioRecorder.stopRecording(() => {
                this.handleRecordingStopped();
                onStopped();
            });
        } catch (error) {
            datadogRum.addError(error);
            this.props.stopRecording();
            this.props.setRecordingError(MIC_FAILURE_TYPES.ACCESS_FAILED);
        }
    };

    saveAudioRecording = async () => {
        const { saveRecordedResult } = this.props;
        const blob = this.audioRecorder.getBlob();

        if (blob) {
            logger.log(`Original blob size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        }

        try {
            const convertedBlob = await convertForAnalysisService(blob);
            saveRecordedResult(convertedBlob);
        } catch (error) {
            logger.error('Conversion failed:', error);
            datadogRum.addError(error);
            logger.log('Falling back to original blob');
            saveRecordedResult(blob); // Fall back to original
        }
    };

    handleRecordingStopped = () => {
        this.props.stopRecording();
        this.saveAudioRecording();
    };

    cleanupResources = () => {
        if (this.audioRecorder) {
            try {
                const state = this.audioRecorder.getState();
                logger.log(`Cleaning up recorder - State: ${state}`);
                if (state === 'recording') {
                    this.audioRecorder.stopRecording(() => {});
                }
                this.audioRecorder.destroy();
                this.audioRecorder = null;
                logger.log('Recorder destroyed successfully');
            } catch (error) {
                logger.error('Cleanup error:', error);
                datadogRum.addError(error);
                this.audioRecorder = null;
            }
        }
        this.isInitializing = false;
    };

    render() {
        return null;
    }
}

RecordingView.propTypes = {
    duration: PropTypes.number.isRequired,
    startRecording: PropTypes.func,
    stopRecording: PropTypes.func,
    saveRecordedResult: PropTypes.func,
    setRecordingError: PropTypes.func,
    setRecordingStream: PropTypes.func,
    recordingStream: PropTypes.shape({
        clone: PropTypes.func.isRequired,
        active: PropTypes.bool.isRequired,
        getAudioTracks: PropTypes.func.isRequired,
    }),
};

RecordingView.defaultProps = {
    startRecording: () => {},
    stopRecording: () => {},
    setRecordingStream: () => {},
    setRecordingError: () => {},
    saveRecordedResult: () => {},
    recordingStream: undefined,
};

export default React.forwardRef((props, ref) => <RecordingView {...props} ref={ref} />);
